import { TransactionResponse } from '@ethersproject/abstract-provider';
import erc721Abi from '@manifoldxyz/typed-contracts/abis/ERC721Creator.json';
import BaseContract from './baseContract';

export interface ContractError {
  code: string;
  cancelled: boolean;
  replacement: {
    hash: string;
  };
}

export default class CreatorContract extends BaseContract {
  contractAddress: string;
  // @dev: shouldn't matter which ABI we use since this is for shared behavior
  ABI = erc721Abi;

  constructor(contractAddress: string) {
    super();
    this.contractAddress = contractAddress;
  }

  async isApprovedForAll(owner: string, operator: string): Promise<boolean> {
    return await this._callWeb3WithServerFallback(
      this.contractAddress,
      this.ABI,
      'isApprovedForAll(address,address)',
      [owner, operator],
      false,
    );
  }

  /*
   * Always sets `approved` to true for the given `operator`
   */
  async setApprovalForAll(
    account: string,
    operator: string,
  ): Promise<TransactionResponse | undefined> {
    try {
      // skip if there is no point
      const alreadyApproved = await this.isApprovedForAll(account, operator);
      if (alreadyApproved) {
        return;
      }

      // overestimate gas by 1.25x
      //
      // We have allowlisted calls to `eth_estimateGas` on `safeTransferFrom` signature
      // in manifold provider service. This way, estimation will work in the case
      // of WC provider that cant estimate gas. Otherwise this call  will be made
      // using the browser provider. If all of those fail, we will error.
      const gasEstimate = await this._estimateGas3WithServerFallback(
        this.contractAddress,
        this.ABI,
        'setApprovalForAll',
        [operator, true, { from: account }],
      );

      // On WalletConnect provider, you can only make eth_call calls.
      // This means we have to pass in `unchecked` to ethersjs to
      // sidestep the automatic gas estimation that happens before
      // *all* write calls and then pass in our own gas estimate
      // from before, which in WC's case comes from the manifold
      // provider service.
      let unchecked = false;
      if (
        localStorage.getItem('connectMethod') &&
        localStorage.getItem('connectMethod') === 'walletConnect'
      ) {
        unchecked = true;
      }

      return await this._getContractInstance(
        this.contractAddress,
        this.ABI,
        true,
        false, // no bridge
        unchecked, // skip gas estimation for WC
      ).setApprovalForAll(operator, true, { gasLimit: gasEstimate });
    } catch (e: unknown) {
      await this.errorHandling(e as ContractError);
    }
  }

  async errorHandling(error: ContractError): Promise<void> {
    if (error.code === 'TRANSACTION_REPLACED' && !error.cancelled && error.replacement) {
      const provider = window.ManifoldEthereumProvider.provider();
      if (!provider) {
        throw new Error('No web3 provider detected, please refresh the page and try again');
      }
      const tx = await provider.getTransaction(error.replacement.hash);
      // Possible failed transaction
      if (!tx) {
        throw new Error('Failed Transaction');
      }
      await tx.wait(1);
    } else {
      throw error;
    }
  }
}
